/**
 * Thin fetch wrapper for the Email Wrapper API.
 *
 * - Reads the `csrf_token` cookie and echoes it in the `X-CSRF-Token` header on
 *   mutating methods (double-submit CSRF).
 * - Sends credentials so the `access_token` / `refresh_token` cookies flow.
 * - Parses the uniform error envelope: `{ error: { code, message, details } }`.
 * - On 401 from a non-auth endpoint, attempts a single `/api/auth/refresh`
 *   round-trip and replays the original request once. Concurrent 401s share
 *   one in-flight refresh — otherwise the first rotation revokes the refresh
 *   token and the rest of the burst would fail their own refresh attempts.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  details: Record<string, unknown>;
}

export class ApiError extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? {};
  }
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const REFRESH_PATH = "/api/auth/refresh";
// Endpoints whose 401 must NOT trigger a refresh-then-retry. /refresh would
// recurse; /login 401 means bad credentials (no point refreshing); /logout
// 401 means the session is already gone.
const NO_RETRY_ON_401 = new Set([REFRESH_PATH, "/api/auth/login", "/api/auth/logout"]);

// Empty in dev so the vite proxy at /api still works; absolute URL in prod
// so the browser fetches the API host directly.
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.$?*|{}()[\]\\/+^]/g, "\\$&") + "=([^;]*)"),
  );
  const raw = match?.[1];
  return raw !== undefined ? decodeURIComponent(raw) : null;
}

// --- Refresh coordination -------------------------------------------------

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Fire a single `/api/auth/refresh` and return whether it rotated a fresh
 * pair of cookies. Concurrent callers share the same promise so a burst of
 * 401s causes ONE refresh, not N — otherwise the first call revokes the
 * refresh token and the rest race into 401.
 */
function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const url = `${API_BASE}${REFRESH_PATH}`;
  refreshInFlight = (async () => {
    try {
      const res = await fetch(url, { method: "POST", credentials: "include" });
      return res.ok;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// Auth-store hook: when the refresh ultimately fails, we want the auth store
// to flip to `unauthenticated` so the next route guard kicks the user back to
// /login. The store registers itself via `setUnauthorizedHandler` to avoid a
// circular import (`stores/auth` already imports this module).
let unauthorizedHandler: (() => void) | null = null;
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

// --- Public API -----------------------------------------------------------

export interface ApiOptions extends RequestInit {
  json?: unknown;
}

export async function apiFetch<T>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const baseHeaders = new Headers(opts.headers ?? {});
  let body = opts.body;

  if (opts.json !== undefined) {
    baseHeaders.set("Content-Type", "application/json");
    body = JSON.stringify(opts.json);
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;

  // Each send() call re-reads the csrf cookie because /refresh rotates it —
  // the retry must use the new value or the CSRF middleware rejects with 403.
  const send = async (): Promise<Response> => {
    const headers = new Headers(baseHeaders);
    if (MUTATING.has(method)) {
      const csrf = readCookie("csrf_token");
      if (csrf) headers.set("X-CSRF-Token", csrf);
    }
    return fetch(url, {
      ...opts,
      method,
      headers,
      body,
      credentials: "include",
    });
  };

  let res = await send();

  if (res.status === 401 && !NO_RETRY_ON_401.has(path)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await send();
    }
  }

  if (res.status === 401 && path !== "/api/auth/login") {
    // Refresh wasn't attempted (auth-only endpoint), or refresh failed, or the
    // retry still 401'd. Either way the session is dead — let the auth store
    // know so the next navigation hits /login.
    unauthorizedHandler?.();
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (parsed as { error?: ApiErrorBody } | null)?.error;
    if (err) throw new ApiError(res.status, err);
    throw new ApiError(res.status, {
      code: "http_error",
      message: res.statusText || `HTTP ${res.status}`,
      details: {},
    });
  }

  return parsed as T;
}
